import { useChangePassword } from "@/api/mutations/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Message } from "@/components/ui/message";
import { useTheme } from "@/hooks/use-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import type { AxiosError } from "axios";
import { Controller, useForm } from "react-hook-form";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";

const schema = z
  .object({
    newPassword: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

export type ResetPasswordForm = z.infer<typeof schema>;

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useChangePassword();
  const location = useLocation();
  useTheme();

  const state = location.state as {
    password?: string;
  };

  const { control, handleSubmit } = useForm<ResetPasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: ResetPasswordForm) => {
    // Use the password from state as oldPassword
    const payload = {
      oldPassword: state.password!,
      newPassword: values.newPassword,
    };

    mutateAsync(payload)
      .then(() => {
        toast.success("Password updated successfully", {
          description:
            "Your password has been changed. You can now use your new password to log in.",
        });
        navigate("/dashboard", { replace: true });
      })
      .catch((error: AxiosError<{ message: string }, unknown>) => {
        const errorMessage =
          error?.response?.data?.message || error?.message || "Unable to update password";

        toast.error("Password update failed", {
          description: errorMessage,
        });
      });
  };

  if (!state?.password) {
    return <Navigate to="/login" />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background text-foreground">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle role="heading">Set new password before login</CardTitle>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Controller
              name="newPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    placeholder="Enter new password"
                    disabled={isPending}
                    {...field}
                  />
                  {error?.message && <Message variant="error">{error.message}</Message>}
                </>
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <>
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    placeholder="Confirm new password"
                    disabled={isPending}
                    {...field}
                  />
                  {error?.message && <Message variant="error">{error.message}</Message>}
                </>
              )}
            />

            <Button type="submit" disabled={isPending} className="w-full">
              {isPending ? "Updating..." : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
